/**
 * Mobile App Store Screenshots
 *
 * One row per image in `MobileAppStoreScreenshot`, ordered inside the set for
 * one app, store, locale, release line and device class (SCH-1945 KTD1, KTD2;
 * release lines U12).
 *
 * A SET IS A GROUP, NOT A ROW. There is no set table and no unique constraint
 * behind any of this: `(mobileAppId, store, locale, lineId, deviceClass)`
 * is the grouping key, position is dense only because the backend service keeps
 * it dense under a parent-row lock, and nothing here may be written as if the
 * database enforced either. This module is the catalog and the contract; the
 * counting, locking and ordering live in the backend service.
 *
 * NEVER DEVICE-FACING. Nothing here reaches a device or a build. A staff
 * release on a line (or the setup checklist's push rows) sends one line's sets to
 * Apple or Google; saving a set sends nothing. Screenshots are never on
 * the public asset manifest, never in the app-config asset projection, never in
 * the pre-login response and never in readiness (R9); they are read through the
 * authed super-admin endpoint and its `/internal` API-key twin, and nothing
 * else.
 *
 * THE VENDOR RULES ARE DATA HERE, AND THEY ARE THE REFUSAL. A dimension that is
 * one pixel off an accepted size is a hard refusal on both lanes with no
 * override (R5): when a vendor adds a display size, the remedy is an edit to
 * `STORE_SCREENSHOT_DEVICE_CLASSES`, not a bypass flag.
 *
 * Zod only, no neverthrow: `validateScreenshotMetadata` returns a plain
 * discriminated union so the web pre-flight can call it directly, and the
 * backend commit core wraps the same result in a `Result`.
 */

import { z } from 'zod'
import { AssetUploadSignatureResponseSchema } from './mobile-app-asset'
import { compareVersions } from './mobile-deployment'
import { StoreListingLocaleSchema, StoreListingStoreSchema, type StoreListingStore } from './mobile-app-store-listing'

// =============================================================================
// Vendor sources
//
// Cited beside the entries they govern, so a future edit lands on the page that
// justifies the number rather than on a search result.
// =============================================================================

/** Apple's current screenshot specification page, read 2026-09-18. */
export const APPLE_SCREENSHOT_SPEC_URL =
  'https://developer.apple.com/help/app-store-connect/reference/screenshot-specifications'

/** Google Play's preview-asset ("graphic assets") page, read 2026-09-18. */
export const PLAY_SCREENSHOT_SPEC_URL = 'https://support.google.com/googleplay/android-developer/answer/9866151'

/** JPEG and PNG at both stores. Neither accepts WebP, and neither accepts alpha. */
const SCREENSHOT_MIME_TYPES = ['image/jpeg', 'image/png'] as const

/**
 * Apple publishes no byte cap for a screenshot. 15 MB is the value the shipped
 * `play-feature-graphic` store slot already uses and the cap the internal lane's
 * `sourceUrl` fetch enforces, so a file that passes here cannot fail the fetch
 * guard for a reason the operator never sees (plan R2).
 */
const APPLE_MAX_SIZE_BYTES = 15 * 1024 * 1024

/** Play's own cap for a preview asset. */
const PLAY_MAX_SIZE_BYTES = 8 * 1024 * 1024

// =============================================================================
// Catalog
// =============================================================================

/** An exact accepted size. Apple accepts a fixed list; Play accepts a range. */
export interface StoreScreenshotSize {
  readonly width: number
  readonly height: number
}

/**
 * How a class decides whether a pair of dimensions is acceptable.
 *
 *  - `exact-sizes`: the width/height pair must equal one of `sizes`. Apple's
 *    rule, and the reason a near miss is a refusal rather than a warning.
 *  - `aspect-range`: each side must fall inside `[minSide, maxSide]`, and then
 *    EITHER the ratio must be one of `aspectRatios` (when the vendor names
 *    exact ratios) OR the long side must be at most `maxLongSideRatio` times
 *    the short one (when the vendor states a bound instead). Play's rule is the
 *    bound; both fields are optional so a class states only what its vendor does.
 */
export type StoreScreenshotDimensionRule =
  | { readonly kind: 'exact-sizes'; readonly sizes: readonly StoreScreenshotSize[] }
  | {
      readonly kind: 'aspect-range'
      readonly aspectRatios?: readonly StoreScreenshotAspectRatio[]
      /** Long side ÷ short side, at most. Play states 2. */
      readonly maxLongSideRatio?: number
      readonly minSide: number
      readonly maxSide: number
    }

/** An accepted ratio, as the label an operator reads and the numbers we compare. */
export interface StoreScreenshotAspectRatio {
  readonly label: string
  readonly width: number
  readonly height: number
}

/**
 * PLAY STATES A BOUND, NOT A LIST OF RATIOS (SCH-2081).
 *
 * This was first written as exactly 16:9 or 9:16, which is stricter than Play and rejected sizes
 * Play accepts and this fleet already ships: 1200 × 1920 (Nexus 7) and 1600 × 2560 (Nexus 10) are
 * both 5:8, and both were refused. The slot catalog this replaces had it right —
 * `mobile-app-asset.ts` records the rule as "320–3840 px per side with the long side at most twice
 * the short", taken from the vendor. Narrowing a vendor rule is not a safe default: it refuses
 * valid art with an error that tells the operator to resize correct files.
 */
const PLAY_MAX_LONG_SIDE_RATIO = 2

export interface StoreScreenshotClassSpec {
  readonly store: StoreListingStore
  readonly deviceClass: string
  readonly label: string
  /** Apple's App Store Connect display type, absent on Play. The push unit keys on it. */
  readonly appleDisplayType?: string
  readonly dimensions: StoreScreenshotDimensionRule
  /** The vendor minimum for a complete set (R11). Zero means the class is optional. */
  readonly minCount: number
  readonly maxCount: number
  readonly maxSizeBytes: number
  readonly acceptedMimeTypes: readonly string[]
  readonly requireOpaque: true
  readonly vendorUrl: string
}

/** Both orientations of a size: Apple accepts the landscape transpose of each portrait size. */
const withTranspose = (sizes: readonly StoreScreenshotSize[]): readonly StoreScreenshotSize[] =>
  sizes.flatMap(size => [size, { width: size.height, height: size.width }])

/**
 * The per-store, per-device-class rules, as data (R2).
 *
 * Apple's sizes are the art-pipeline strategy's, which the 2026-09-18 doc review
 * confirmed. Apple's spec page also lists 1260x2736 for the 6.9-inch display,
 * which is not in this table; the PR records that disagreement rather than
 * resolving it silently, because widening an accepted-size list is a decision
 * about what we will ship, not a transcription.
 *
 * An iPad class is stocked alongside the iPhone ones because every school app is
 * a universal build — `ios.supportsTablet: true` is fleet-wide in
 * `apps/mobile/app.config.ts` — so Apple requires an iPad set as well.
 *
 * NEITHER IPHONE CLASS IS REQUIRED ON ITS OWN (SCH-2092). Apple takes ONE
 * iPhone size and derives the rest, and it accepts either 6.5" or 6.9" as that
 * base — so "6.5" is required" and "6.9" is required" are both wrong, and each
 * one marks a listing Apple would take as incomplete. The catalog first stocked
 * only 6.9", whose `exact-sizes` rule REFUSED every screenshot the studio
 * produced; SCH-2088 then swapped the requirement to 6.5", which flagged an app
 * holding only 6.9" art as below the minimum. Both classes now sit at
 * `minCount: 0` and the real rule lives in `STORE_SCREENSHOT_ALTERNATIVE_GROUPS`
 * below, which is the only thing that can express "at least one of these".
 *
 * 6.5" is still what `screenshot-studio/targets.ts` renders — it measured the
 * size off the shipped source files (`iphone_6.5_*.png`) and records why the
 * aspect ratio cannot tell 6.5" from 6.7" apart — so an app prepared by the
 * studio satisfies the group through 6.5".
 *
 * Play's tablet classes sit at `minCount: 0` with no group above them: Play
 * accepts a listing without them, and a class at zero always reads met (see
 * `screenshotMinimumMet`).
 */
export const STORE_SCREENSHOT_DEVICE_CLASSES = {
  apple: {
    'iphone-6.5': {
      store: 'apple',
      deviceClass: 'iphone-6.5',
      label: '6.5" iPhone',
      appleDisplayType: 'APP_IPHONE_65',
      dimensions: {
        kind: 'exact-sizes',
        // 1242x2688 is 6.5"; 1284x2778 is 6.7". Their aspect ratios agree to four decimal places,
        // so a published screenshot cannot be measured to tell them apart — the number comes from
        // the fleet's own source art. Both are listed because Apple accepts either under
        // APP_IPHONE_65 and a hand upload may be at the 6.7" size.
        sizes: withTranspose([
          { width: 1242, height: 2688 },
          { width: 1284, height: 2778 }
        ])
      },
      // Zero, not one: the iPhone requirement is "6.5 OR 6.9", which a per-class
      // minimum cannot say. `STORE_SCREENSHOT_ALTERNATIVE_GROUPS` carries it.
      minCount: 0,
      maxCount: 10,
      maxSizeBytes: APPLE_MAX_SIZE_BYTES,
      acceptedMimeTypes: SCREENSHOT_MIME_TYPES,
      requireOpaque: true,
      vendorUrl: APPLE_SCREENSHOT_SPEC_URL
    },
    'iphone-6.9': {
      store: 'apple',
      deviceClass: 'iphone-6.9',
      label: '6.9" iPhone',
      appleDisplayType: 'APP_IPHONE_67',
      dimensions: {
        kind: 'exact-sizes',
        sizes: withTranspose([
          { width: 1320, height: 2868 },
          { width: 1290, height: 2796 },
          { width: 1206, height: 2622 }
        ])
      },
      // Zero for the same reason as 6.5": either size satisfies Apple, and the
      // group below is what holds the pair to one. The studio renders 6.5", but
      // hand-uploaded 6.9" art is a complete iPhone set on its own.
      minCount: 0,
      maxCount: 10,
      maxSizeBytes: APPLE_MAX_SIZE_BYTES,
      acceptedMimeTypes: SCREENSHOT_MIME_TYPES,
      requireOpaque: true,
      vendorUrl: APPLE_SCREENSHOT_SPEC_URL
    },
    'ipad-13': {
      store: 'apple',
      deviceClass: 'ipad-13',
      label: '13" iPad',
      appleDisplayType: 'APP_IPAD_PRO_3GEN_129',
      dimensions: {
        kind: 'exact-sizes',
        sizes: withTranspose([
          { width: 2064, height: 2752 },
          { width: 2048, height: 2732 }
        ])
      },
      minCount: 1,
      maxCount: 10,
      maxSizeBytes: APPLE_MAX_SIZE_BYTES,
      acceptedMimeTypes: SCREENSHOT_MIME_TYPES,
      requireOpaque: true,
      vendorUrl: APPLE_SCREENSHOT_SPEC_URL
    }
  },
  play: {
    phone: {
      store: 'play',
      deviceClass: 'phone',
      label: 'Phone',
      dimensions: {
        kind: 'aspect-range',
        maxLongSideRatio: PLAY_MAX_LONG_SIDE_RATIO,
        minSide: 320,
        maxSide: 3840
      },
      minCount: 2,
      maxCount: 8,
      maxSizeBytes: PLAY_MAX_SIZE_BYTES,
      acceptedMimeTypes: SCREENSHOT_MIME_TYPES,
      requireOpaque: true,
      vendorUrl: PLAY_SCREENSHOT_SPEC_URL
    },
    'tablet-7': {
      store: 'play',
      deviceClass: 'tablet-7',
      label: '7" tablet',
      dimensions: {
        kind: 'aspect-range',
        maxLongSideRatio: PLAY_MAX_LONG_SIDE_RATIO,
        minSide: 1080,
        maxSide: 7680
      },
      minCount: 0,
      maxCount: 8,
      maxSizeBytes: PLAY_MAX_SIZE_BYTES,
      acceptedMimeTypes: SCREENSHOT_MIME_TYPES,
      requireOpaque: true,
      vendorUrl: PLAY_SCREENSHOT_SPEC_URL
    },
    'tablet-10': {
      store: 'play',
      deviceClass: 'tablet-10',
      label: '10" tablet',
      dimensions: {
        kind: 'aspect-range',
        maxLongSideRatio: PLAY_MAX_LONG_SIDE_RATIO,
        minSide: 1080,
        maxSide: 7680
      },
      minCount: 0,
      maxCount: 8,
      maxSizeBytes: PLAY_MAX_SIZE_BYTES,
      acceptedMimeTypes: SCREENSHOT_MIME_TYPES,
      requireOpaque: true,
      vendorUrl: PLAY_SCREENSHOT_SPEC_URL
    }
  }
} as const satisfies Record<StoreListingStore, Record<string, StoreScreenshotClassSpec>>

export type AppleScreenshotDeviceClass = keyof (typeof STORE_SCREENSHOT_DEVICE_CLASSES)['apple']
export type PlayScreenshotDeviceClass = keyof (typeof STORE_SCREENSHOT_DEVICE_CLASSES)['play']
export type StoreScreenshotDeviceClass = AppleScreenshotDeviceClass | PlayScreenshotDeviceClass

/** Every class of one store, in the order the panel shows them. */
export const storeScreenshotDeviceClasses = (store: StoreListingStore): readonly StoreScreenshotClassSpec[] =>
  Object.values(STORE_SCREENSHOT_DEVICE_CLASSES[store])

/** Every class of both stores. */
export const STORE_SCREENSHOT_CLASS_LIST: readonly StoreScreenshotClassSpec[] = [
  ...storeScreenshotDeviceClasses('apple'),
  ...storeScreenshotDeviceClasses('play')
]

export const STORE_SCREENSHOT_DEVICE_CLASS_NAMES = STORE_SCREENSHOT_CLASS_LIST.map(
  spec => spec.deviceClass
) as StoreScreenshotDeviceClass[]

/**
 * A device class name, unqualified by store. `phone` belongs to Play and
 * `iphone-6.9` to Apple, and nothing in the union says so — every read still has
 * to go through `getStoreScreenshotClass(store, deviceClass)`, which is what
 * refuses an Apple class sent with `store: 'play'`.
 */
export const StoreScreenshotDeviceClassSchema = z.enum(
  STORE_SCREENSHOT_DEVICE_CLASS_NAMES as [StoreScreenshotDeviceClass, ...StoreScreenshotDeviceClass[]]
)

/** The spec for one store's class, or `undefined` when the pair does not exist. */
export function getStoreScreenshotClass(store: string, deviceClass: string): StoreScreenshotClassSpec | undefined {
  const byClass = (STORE_SCREENSHOT_DEVICE_CLASSES as Record<string, Record<string, StoreScreenshotClassSpec>>)[store]
  return byClass?.[deviceClass]
}

/**
 * Whether a set of `count` images meets its class's vendor minimum (R11).
 *
 * THE ONLY PLACE THE MINIMUM IS COMPARED. A class at `minCount: 0` always reads
 * met, and the Overview badge, the count badge and any later readiness arm all
 * have to get that right — so none of them reimplements the comparison.
 *
 * An unknown store/class pair reads NOT met: a set we cannot find a rule for is
 * not one we can certify as complete.
 */
export function screenshotMinimumMet(store: string, deviceClass: string, count: number): boolean {
  const spec = getStoreScreenshotClass(store, deviceClass)
  return spec ? count >= spec.minCount : false
}

// =============================================================================
// Alternative groups (SCH-2092)
// =============================================================================

/**
 * A vendor rule of the form "at least one of these device classes" — the one
 * shape `minCount` cannot express.
 *
 * A per-class minimum can only say "this class needs N". Apple's iPhone rule is
 * "one iPhone set, at 6.5 OR 6.9", so pinning the minimum to either class
 * reports a listing Apple would accept as incomplete. The classes in a group
 * therefore sit at `minCount: 0` and the group carries the requirement.
 *
 * `minCount` here is a count of SATISFIED CLASSES, not of images: a group is met
 * when at least that many of its classes hold at least one image each. One image
 * is the threshold because a class inside a group has no per-class minimum left
 * to compare against.
 */
export interface StoreScreenshotAlternativeGroup {
  readonly id: string
  /** How the requirement is stated to an operator. */
  readonly label: string
  readonly deviceClasses: readonly string[]
  /** How many of `deviceClasses` must hold images. One today, for every group. */
  readonly minCount: number
  readonly vendorUrl: string
}

/**
 * Every "at least one of" rule, by store.
 *
 * Apple only. Play states a per-class minimum for phones and nothing at all for
 * its two tablet sizes, so no Play class needs a group; an empty array is the
 * honest entry rather than an omitted key.
 */
export const STORE_SCREENSHOT_ALTERNATIVE_GROUPS = {
  apple: [
    {
      id: 'iphone',
      label: 'iPhone',
      // ORDER IS THE PREFERENCE ORDER, read by nothing that enforces it but by
      // the copy that names a size when neither is prepared. 6.5" comes first
      // because it is what the studio renders.
      deviceClasses: ['iphone-6.5', 'iphone-6.9'],
      minCount: 1,
      vendorUrl: APPLE_SCREENSHOT_SPEC_URL
    }
  ],
  play: []
} as const satisfies Record<StoreListingStore, readonly StoreScreenshotAlternativeGroup[]>

/** Every alternative group of one store. */
export function storeScreenshotAlternativeGroups(store: string): readonly StoreScreenshotAlternativeGroup[] {
  return (
    (STORE_SCREENSHOT_ALTERNATIVE_GROUPS as Record<string, readonly StoreScreenshotAlternativeGroup[]>)[store] ?? []
  )
}

/** The group a class belongs to, or `undefined` when it stands on its own minimum. */
export function alternativeGroupForClass(
  store: string,
  deviceClass: string
): StoreScreenshotAlternativeGroup | undefined {
  return storeScreenshotAlternativeGroups(store).find(group => group.deviceClasses.includes(deviceClass))
}

/** Whether one group is satisfied by the images a store holds, keyed by device class. */
export function screenshotAlternativeGroupMet(
  group: StoreScreenshotAlternativeGroup,
  countByDeviceClass: Readonly<Record<string, number>>
): boolean {
  const satisfied = group.deviceClasses.filter(deviceClass => (countByDeviceClass[deviceClass] ?? 0) > 0).length
  return satisfied >= group.minCount
}

/**
 * Whether a whole store's set is complete: every class meets its own minimum AND
 * every alternative group is satisfied.
 *
 * THE ONLY PLACE A STORE-LEVEL VERDICT IS FORMED. The Overview row, the
 * Screenshots panel and any later push gate all read this one function — the
 * per-class `screenshotMinimumMet` answers a narrower question and cannot see a
 * group at all, so a caller that ANDs it across a store gets Apple's iPhone rule
 * wrong in both directions.
 *
 * A class the caller did not mention counts as zero images, which is what makes
 * an app with nothing prepared read as not met rather than vacuously met.
 */
export function storeScreenshotMinimumsMet(
  store: string,
  countByDeviceClass: Readonly<Record<string, number>>
): boolean {
  const classesMet = STORE_SCREENSHOT_CLASS_LIST.filter(spec => spec.store === store).every(spec =>
    screenshotMinimumMet(store, spec.deviceClass, countByDeviceClass[spec.deviceClass] ?? 0)
  )
  if (!classesMet) return false
  return storeScreenshotAlternativeGroups(store).every(group =>
    screenshotAlternativeGroupMet(group, countByDeviceClass)
  )
}

/**
 * How a class states its own requirement in one phrase: its minimum, or the
 * group it shares the requirement with.
 *
 * A class inside a group reads "optional" on its own numbers, which is exactly
 * the wrong thing to show beside 6.5" when Apple will refuse a listing with
 * neither iPhone size.
 */
export function describeScreenshotClassMinimum(store: string, deviceClass: string): string {
  const spec = getStoreScreenshotClass(store, deviceClass)
  if (!spec) return 'optional'
  const group = alternativeGroupForClass(store, deviceClass)
  if (group) {
    const alternatives = group.deviceClasses
      .filter(candidate => candidate !== deviceClass)
      .map(candidate => getStoreScreenshotClass(store, candidate)?.label ?? candidate)
    return alternatives.length > 0 ? `at least 1 here or in ${alternatives.join(' or ')}` : `at least ${group.minCount}`
  }
  return spec.minCount > 0 ? `at least ${spec.minCount}` : 'optional'
}

// =============================================================================
// Version strings (R3)
// =============================================================================

/**
 * What `compareVersions` in `mobile-deployment.ts` can actually order: dot-
 * separated numeric segments, at least one of them. That comparator returns
 * `null` for anything else, so accepting `1.9.0-beta` or `v1.9.0` here would
 * store versions the picker cannot sort and the "newest prepared version" could
 * not be derived from.
 *
 * Segments are capped at nine digits, which is NARROWER than the comparator
 * reads. `compareVersions` parses each segment with `Number`, so a segment past
 * 2^53 loses precision and two different versions can compare equal; nine digits
 * is comfortably inside that and far past any real marketing version.
 *
 * Keep this pattern and that comparator in step; the pattern is deliberately no
 * wider than what the comparator can order CORRECTLY.
 */
export const STORE_SCREENSHOT_VERSION_PATTERN = /^\d{1,9}(\.\d{1,9})*$/

/** Matches `MobileAppStoreScreenshot.versionString`, `VarChar(64)`. */
export const STORE_SCREENSHOT_VERSION_MAX_LENGTH = 64

export const STORE_SCREENSHOT_VERSION_RULE =
  'A version must be numbers separated by dots, such as 1.9.0, with at most nine digits in each part. Suffixes and prefixes are not accepted: write 1.9.0, not v1.9.0 or 1.9.0-beta.'

export const StoreScreenshotVersionSchema = z
  .string()
  .min(1, { message: STORE_SCREENSHOT_VERSION_RULE })
  .max(STORE_SCREENSHOT_VERSION_MAX_LENGTH, { message: STORE_SCREENSHOT_VERSION_RULE })
  .regex(STORE_SCREENSHOT_VERSION_PATTERN, { message: STORE_SCREENSHOT_VERSION_RULE })

/**
 * The version a store REPORTED, read as a version a set can be keyed on — or
 * `null` when there is none in it (SCH-2092).
 *
 * WHY THIS EXISTS. `MobileAppStoreVersion.versionString` holds whatever the
 * store said, and Play says the RELEASE NAME: `5 (1.8.17)`, `128 (1.8.0)`,
 * sometimes `v1.8.15`. Those are the ledger's facts and stay as they are. But
 * the code that decides which version to prepare or push needs the marketing
 * version out of them, and testing the raw text against
 * `STORE_SCREENSHOT_VERSION_PATTERN` rejected fourteen of the fleet's sixteen
 * apps — which then silently prepared every set under a default version and
 * pushed none of them.
 *
 * READS, NEVER WRITES. Nothing here rewrites a stored row: this is the parse on
 * the way out of the ledger. Two accepted shapes and nothing else —
 *
 *   - the whole string is already a version: `1.8.17`
 *   - a `v` prefix, which Play release names carry: `v1.8.15`
 *   - a version inside parentheses, which is Play's `<code> (<name>)` form
 *
 * A string with no parenthesised version and no leading version returns `null`,
 * and the caller reports that rather than inventing one. A parenthesised
 * candidate WINS over a leading bare number, because `5 (1.8.17)` leads with the
 * version code — taking the `5` would key every set of that app on `5`.
 */
export function normalizeStoreScreenshotVersion(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string') return null
  const text = raw.trim()
  if (text.length === 0) return null

  const accept = (candidate: string): string | null => {
    const stripped = candidate.trim().replace(/^v/i, '')
    if (stripped.length > STORE_SCREENSHOT_VERSION_MAX_LENGTH) return null
    return STORE_SCREENSHOT_VERSION_PATTERN.test(stripped) ? stripped : null
  }

  const parenthesised = /\(([^)]*)\)/.exec(text)
  return (parenthesised ? accept(parenthesised[1] ?? '') : null) ?? accept(text)
}

/**
 * Prepared versions, newest first — THE ONE ORDERING (SCH-2092).
 *
 * Four copies of this sort existed across the push resolver, the studio
 * resolver, the web picker and the "copy from" source, which is three chances
 * for "the newest prepared version" to mean something different depending on
 * which surface was asked.
 *
 * `compareVersions` returns `null` for a pair it cannot order. The schema
 * refuses such strings on the way in, but a row written before that schema
 * existed can still carry one, so fall back to a name sort rather than leaving
 * the order undefined. Duplicates collapse.
 */
export function orderStoreScreenshotVersions(versions: Iterable<string>): string[] {
  return [...new Set(versions)].sort((a, b) => compareVersions({ version: b }, { version: a }) ?? a.localeCompare(b))
}

// =============================================================================
// Validation
// =============================================================================

/**
 * Why a commit was refused. The field shapes mirror the backend's
 * `AssetValidationError` (`apps/backend/src/services/mobile-apps/asset-slots.ts`)
 * so the two error vocabularies stay legible side by side, with the slot name
 * replaced by the store/class pair that identifies a screenshot set.
 *
 * Every variant carries what `describeScreenshotValidationError` needs to state
 * the rule and the fix (R5); nothing renders a refusal from the type alone.
 */
export type ScreenshotValidationError =
  | { type: 'UNKNOWN_DEVICE_CLASS'; store: string; deviceClass: string }
  | {
      type: 'MIME_TYPE_NOT_ACCEPTED'
      store: string
      deviceClass: string
      mimeType: string
      accepted: readonly string[]
    }
  | { type: 'SIZE_EXCEEDS_LIMIT'; store: string; deviceClass: string; sizeBytes: number; max: number }
  | { type: 'DIMENSIONS_REQUIRED'; store: string; deviceClass: string }
  | {
      type: 'DIMENSIONS_NOT_ACCEPTED'
      store: string
      deviceClass: string
      width: number
      height: number
      accepted: readonly StoreScreenshotSize[]
    }
  | {
      type: 'ASPECT_RATIO_NOT_ACCEPTED'
      store: string
      deviceClass: string
      width: number
      height: number
      accepted: readonly StoreScreenshotAspectRatio[]
    }
  | {
      type: 'LONG_SIDE_RATIO_EXCEEDED'
      store: string
      deviceClass: string
      width: number
      height: number
      max: number
    }
  | {
      type: 'DIMENSION_OUT_OF_RANGE'
      store: string
      deviceClass: string
      field: 'width' | 'height'
      value: number
      min: number
      max: number
    }
  | { type: 'ALPHA_NOT_ALLOWED'; store: string; deviceClass: string }
  | { type: 'COUNT_EXCEEDS_LIMIT'; store: string; deviceClass: string; current: number; max: number }

export type ScreenshotValidationResult =
  { ok: true; spec: StoreScreenshotClassSpec } | { ok: false; error: ScreenshotValidationError }

export interface ScreenshotMetadata {
  width?: number | null
  height?: number | null
  mimeType: string
  sizeBytes: number
  /**
   * Whether the image carries an alpha channel, when the caller measured it.
   *
   * Neither store accepts transparency, and every class says so with
   * `requireOpaque`. Metadata alone cannot tell: Cloudinary's upload response
   * does not report alpha, so the super-admin lane learns it from the web byte
   * pre-flight, which reads the PNG IHDR, and leaves this undefined otherwise.
   * UNDEFINED IS NOT "OPAQUE" — it is "nobody looked", and it passes, exactly as
   * the slot validator treats it today. A caller that did look is held to it.
   */
  hasAlpha?: boolean | null
}

const sizeMatches = (sizes: readonly StoreScreenshotSize[], width: number, height: number): boolean =>
  sizes.some(size => size.width === width && size.height === height)

const aspectMatches = (ratios: readonly StoreScreenshotAspectRatio[], width: number, height: number): boolean =>
  ratios.some(ratio => width * ratio.height === height * ratio.width)

/**
 * The dimension half of the check, split out so the vendor-shape rules live in one place and
 * `validateScreenshotMetadata` stays a flat sequence of guards.
 *
 * Returns the refusal, or `undefined` when the size is acceptable.
 */
function refuseDimensions(
  spec: StoreScreenshotClassSpec,
  width: number,
  height: number
): ScreenshotValidationError | undefined {
  const { store, deviceClass } = spec

  if (spec.dimensions.kind === 'exact-sizes') {
    if (sizeMatches(spec.dimensions.sizes, width, height)) return undefined
    return { type: 'DIMENSIONS_NOT_ACCEPTED', store, deviceClass, width, height, accepted: spec.dimensions.sizes }
  }

  const { aspectRatios, maxLongSideRatio, minSide, maxSide } = spec.dimensions
  for (const [field, value] of [
    ['width', width],
    ['height', height]
  ] as const) {
    if (value < minSide || value > maxSide) {
      return { type: 'DIMENSION_OUT_OF_RANGE', store, deviceClass, field, value, min: minSide, max: maxSide }
    }
  }

  if (aspectRatios && !aspectMatches(aspectRatios, width, height)) {
    return { type: 'ASPECT_RATIO_NOT_ACCEPTED', store, deviceClass, width, height, accepted: aspectRatios }
  }

  // `>` on the product rather than on a quotient: the bound is exact (Play's is 2), and a float
  // divide would refuse a legitimately 2:1 image on a rounding error.
  if (maxLongSideRatio !== undefined && Math.max(width, height) > Math.min(width, height) * maxLongSideRatio) {
    return { type: 'LONG_SIDE_RATIO_EXCEEDED', store, deviceClass, width, height, max: maxLongSideRatio }
  }

  return undefined
}

/**
 * The one metadata check both lanes run (R5).
 *
 * Metadata-only by design, like the slot commit: the super-admin lane trusts the
 * width, height and mime the browser read off Cloudinary's upload response, and
 * the internal lane's `sourceUrl` and bytes paths call this with what Cloudinary
 * measured, so those two cannot be lied to. Nothing here reads the file.
 *
 * Dimensions are required, not optional as they are for a slot: a screenshot has
 * no vector format to excuse a missing size, and the whole rule is the size.
 */
export function validateScreenshotMetadata(
  store: string,
  deviceClass: string,
  metadata: ScreenshotMetadata
): ScreenshotValidationResult {
  const spec = getStoreScreenshotClass(store, deviceClass)
  if (!spec) return { ok: false, error: { type: 'UNKNOWN_DEVICE_CLASS', store, deviceClass } }

  if (!spec.acceptedMimeTypes.includes(metadata.mimeType)) {
    return {
      ok: false,
      error: {
        type: 'MIME_TYPE_NOT_ACCEPTED',
        store,
        deviceClass,
        mimeType: metadata.mimeType,
        accepted: spec.acceptedMimeTypes
      }
    }
  }

  if (metadata.sizeBytes > spec.maxSizeBytes) {
    return {
      ok: false,
      error: {
        type: 'SIZE_EXCEEDS_LIMIT',
        store,
        deviceClass,
        sizeBytes: metadata.sizeBytes,
        max: spec.maxSizeBytes
      }
    }
  }

  if (spec.requireOpaque && metadata.hasAlpha === true) {
    return { ok: false, error: { type: 'ALPHA_NOT_ALLOWED', store, deviceClass } }
  }

  const { width, height } = metadata
  if (width == null || height == null) {
    return { ok: false, error: { type: 'DIMENSIONS_REQUIRED', store, deviceClass } }
  }

  const refusal = refuseDimensions(spec, width, height)
  return refusal ? { ok: false, error: refusal } : { ok: true, spec }
}

/** Refuses a commit that would take the set past its vendor maximum. */
export function validateScreenshotCount(
  store: string,
  deviceClass: string,
  currentCount: number,
  adding = 1
): ScreenshotValidationResult {
  const spec = getStoreScreenshotClass(store, deviceClass)
  if (!spec) return { ok: false, error: { type: 'UNKNOWN_DEVICE_CLASS', store, deviceClass } }
  if (currentCount + adding > spec.maxCount) {
    return {
      ok: false,
      error: { type: 'COUNT_EXCEEDS_LIMIT', store, deviceClass, current: currentCount, max: spec.maxCount }
    }
  }
  return { ok: true, spec }
}

const storeScreenshotDeviceClassesFor = (store: string): string => {
  const byClass = (STORE_SCREENSHOT_DEVICE_CLASSES as Record<string, Record<string, StoreScreenshotClassSpec>>)[store]
  return byClass ? Object.keys(byClass).join(', ') : 'apple or play'
}

const formatMegabytes = (bytes: number): string => `${Math.round((bytes / (1024 * 1024)) * 10) / 10} MB`

const formatSizes = (sizes: readonly StoreScreenshotSize[]): string =>
  sizes.map(size => `${size.width}x${size.height}`).join(', ')

/**
 * The refusal an operator reads: the rule that was broken, and what to do about
 * it (R5). Both surfaces render this — the panel's drop zone and the internal
 * lane's error body — so a script and a browser are told the same thing.
 */
export function describeScreenshotValidationError(error: ScreenshotValidationError): { rule: string; fix: string } {
  switch (error.type) {
    case 'UNKNOWN_DEVICE_CLASS':
      return {
        rule: `${error.deviceClass} is not a device class of the ${error.store} store.`,
        fix: `Use one of: ${storeScreenshotDeviceClassesFor(error.store)}.`
      }
    case 'MIME_TYPE_NOT_ACCEPTED':
      return {
        rule: `${error.store} accepts ${error.accepted.join(' and ')}; this file is ${error.mimeType}.`,
        fix: 'Export the image as JPEG or PNG with no transparency and upload it again.'
      }
    case 'SIZE_EXCEEDS_LIMIT':
      return {
        rule: `The limit is ${formatMegabytes(error.max)}; this file is ${formatMegabytes(error.sizeBytes)}.`,
        fix: 'Re-export it at a lower quality, or as JPEG, and upload it again.'
      }
    case 'DIMENSIONS_REQUIRED':
      return {
        rule: 'A screenshot has to state its width and height.',
        fix: 'Upload the file again so its dimensions are measured.'
      }
    case 'DIMENSIONS_NOT_ACCEPTED':
      return {
        rule: `${error.deviceClass} accepts exactly ${formatSizes(error.accepted)}; this image is ${error.width}x${error.height}.`,
        fix: 'Capture or resize it to one of those sizes. A near miss is refused, and there is no override.'
      }
    case 'ASPECT_RATIO_NOT_ACCEPTED':
      return {
        rule: `${error.deviceClass} accepts ${error.accepted.map(ratio => ratio.label).join(' or ')}; this image is ${error.width}x${error.height}.`,
        fix: 'Crop it to one of those ratios and upload it again.'
      }
    case 'LONG_SIDE_RATIO_EXCEEDED':
      return {
        rule: `${error.deviceClass} needs its long side at most ${error.max}x its short side; this image is ${error.width}x${error.height}.`,
        fix: 'Crop it closer to square and upload it again.'
      }
    case 'DIMENSION_OUT_OF_RANGE':
      return {
        rule: `${error.deviceClass} needs a ${error.field} between ${error.min} and ${error.max} pixels; this image is ${error.value}.`,
        fix: 'Resize it inside that range, keeping the accepted aspect ratio.'
      }
    case 'ALPHA_NOT_ALLOWED':
      return {
        rule: `Neither store accepts transparency, and this image has an alpha channel.`,
        fix: 'Flatten it onto a solid background, or export it as JPEG, and upload it again.'
      }
    case 'COUNT_EXCEEDS_LIMIT':
      return {
        rule: `${error.deviceClass} holds at most ${error.max} screenshots and already has ${error.current}.`,
        fix: 'Delete one from the set before uploading another.'
      }
  }
}

// =============================================================================
// Entity
// =============================================================================

/**
 * One `MobileAppStoreScreenshot` row as an API reader sees it. Bigint ids are
 * strings, timestamps are ISO strings, as everywhere else in this package.
 *
 * `locale` stays a plain string, not the single-locale enum: the column already
 * holds whatever a future locale-aware writer puts there, and a reader built for
 * that day should not have to change this schema. The single-locale restriction
 * lives on the REQUEST schemas below, which is where it actually protects the
 * panel (R4).
 *
 * `providerAssetId` and `providerSyncedAt` are declared and unread: the store
 * push units write them, and nothing in SCH-1945 does.
 */
export const MobileAppStoreScreenshotSchema = z.object({
  id: z.string(),
  mobileAppId: z.string(),
  store: StoreListingStoreSchema,
  locale: z.string(),
  versionString: z.string(),
  deviceClass: z.string(),
  position: z.number().int().nonnegative(),
  cloudinaryPublicId: z.string(),
  cloudinaryVersion: z.string(),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  mimeType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  providerAssetId: z.string().nullable(),
  providerSyncedAt: z.string().datetime().nullable(),
  uploadedByUserId: z.string().nullable(),
  uploadedAt: z.string().datetime(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
})
export type MobileAppStoreScreenshot = z.infer<typeof MobileAppStoreScreenshotSchema>

// =============================================================================
// API contracts (KTD3, KTD8)
//
// Defined here and re-exported from `api/mobile-apps/index.ts`, the way the slot
// contracts in `mobile-app-asset.ts` already are.
// =============================================================================

/**
 * The locale a WRITE may name. Single-locale until the panel is locale-aware, so
 * a script cannot create rows the panel cannot show (R4).
 */
export const StoreScreenshotLocaleSchema = StoreListingLocaleSchema

/** The fields that name one set. Every write carries them. */
const storeScreenshotSetTargetShape = {
  store: StoreListingStoreSchema,
  locale: StoreScreenshotLocaleSchema.optional(),
  versionString: StoreScreenshotVersionSchema,
  deviceClass: StoreScreenshotDeviceClassSchema
}

export const STORE_SCREENSHOT_DEVICE_CLASS_PAIR_RULE =
  'That device class does not belong to that store. Apple takes iphone-6.9 and ipad-13; Play takes phone, tablet-7 and tablet-10.'

/**
 * Pairs the class with the store (R1).
 *
 * `deviceClass` is a flat enum across both stores, so nothing in the field types
 * stops `{ store: 'play', deviceClass: 'iphone-6.9' }`. Left unpaired, that pair
 * names a set no catalog entry describes: a commit would be caught by
 * `validateScreenshotMetadata`, but a list, a reorder or a copy would quietly
 * address an empty group instead of refusing. Every request that carries both
 * fields goes through here.
 */
const withDeviceClassForStore = <T extends z.ZodType<{ store: string; deviceClass: string }>>(schema: T) =>
  schema.refine(value => getStoreScreenshotClass(value.store, value.deviceClass) !== undefined, {
    message: STORE_SCREENSHOT_DEVICE_CLASS_PAIR_RULE,
    path: ['deviceClass']
  })

export const StoreScreenshotSetTargetSchema = withDeviceClassForStore(z.object(storeScreenshotSetTargetShape))
export type StoreScreenshotSetTarget = z.infer<typeof StoreScreenshotSetTargetSchema>

/** One image as the panel renders it. `url` is untransformed (R10). */
export const StoreScreenshotSummarySchema = z.object({
  id: z.string(),
  position: z.number().int().nonnegative(),
  url: z.string().url(),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  mimeType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  uploadedAt: z.string().datetime()
})
export type StoreScreenshotSummary = z.infer<typeof StoreScreenshotSummarySchema>

/**
 * One set: the grouping tuple, its images in position order, and whether it
 * meets the vendor minimum. `minimumMet` is served rather than derived on the
 * client so the badge and any later readiness arm read the same verdict — the
 * server computes it with `screenshotMinimumMet`.
 */
export const StoreScreenshotSetSchema = z.object({
  store: StoreListingStoreSchema,
  locale: z.string(),
  versionString: z.string(),
  deviceClass: z.string(),
  minimumMet: z.boolean(),
  screenshots: z.array(StoreScreenshotSummarySchema)
})
export type StoreScreenshotSet = z.infer<typeof StoreScreenshotSetSchema>

/**
 * Which set a store push would send right now, decided by the BACKEND (SCH-2092).
 *
 * SETS ARE KEPT PER RELEASE LINE (release lines U12, R20). The push reads the
 * set of ONE line: on iOS the line of the version being submitted, on Play the
 * line being published (the app's upcoming Android line, else its current
 * one). The plan names that line, so the preview and the push cannot disagree
 * about which line's art goes out.
 *
 * `versionString` is the line's runtime ("1.9.0"), the string a set on that
 * line is addressed by, or `null` when the line holds nothing in this store —
 * "this push would send no screenshots", which is a different fact from "this
 * app has none" and the preview states it as such.
 */
export const StoreScreenshotPushPlanSchema = z.object({
  store: StoreListingStoreSchema,
  versionString: z.string().nullable(),
  locale: z.string(),
  /**
   * The store version the line was resolved FROM, or `null` when the ledger
   * holds none we can read — in which case the app's own line is the answer.
   */
  submittingVersion: z.string().nullable(),
  /** The line the push reads (`AppVersion.publicId`), or `null` when no line resolves. */
  lineId: z.string().nullable(),
  /** That line's display name ("1.9"). */
  lineName: z.string().nullable()
})
export type StoreScreenshotPushPlan = z.infer<typeof StoreScreenshotPushPlanSchema>

/**
 * One release line as the screenshots panel's line picker shows it (U12).
 *
 * `runtimeVersion` is what every set request names the line by: a request's
 * `versionString` is read as a line by its `major.minor`, so "1.9.0" and "1.9.2"
 * both address the 1.9 line's sets.
 *
 * `requiredDeviceClasses` is the line's `screenshotDeviceClasses`: `null` means
 * the line has not decided which classes it needs, and then
 * `incompleteDeviceClasses` is `null` too rather than an empty "all good".
 */
export const StoreScreenshotLineSchema = z.object({
  id: z.string(),
  name: z.string(),
  runtimeVersion: z.string(),
  lifecycle: z.string(),
  /** Images this app holds on the line, in the stores the read covered. */
  imageCount: z.number().int().nonnegative(),
  requiredDeviceClasses: z.array(z.string()).nullable(),
  /** Required classes (of the stores the read covered) whose set is below its minimum. */
  incompleteDeviceClasses: z.array(z.string()).nullable()
})
export type StoreScreenshotLine = z.infer<typeof StoreScreenshotLineSchema>

/**
 * `GET /:id/screenshots`. `versions` is the runtime of every line this app has a
 * set on, newest first; `lines` is every line the picker offers.
 */
export const StoreScreenshotListResponseSchema = z.object({
  versions: z.array(z.string()),
  sets: z.array(StoreScreenshotSetSchema),
  /** One entry per store in scope. Absent on a response from an older backend. */
  push: z.array(StoreScreenshotPushPlanSchema).optional(),
  /** Every line that is still shipped to or holds a set of this app's, newest first. Absent on an older backend. */
  lines: z.array(StoreScreenshotLineSchema).optional()
})
export type StoreScreenshotListResponse = z.infer<typeof StoreScreenshotListResponseSchema>

/** `POST /:id/screenshots/upload-signature`. */
export const StoreScreenshotUploadSignatureRequestSchema = withDeviceClassForStore(
  z.object({
    store: StoreListingStoreSchema,
    versionString: StoreScreenshotVersionSchema,
    deviceClass: StoreScreenshotDeviceClassSchema
  })
)
export type StoreScreenshotUploadSignatureRequest = z.infer<typeof StoreScreenshotUploadSignatureRequestSchema>

/** Same shape the slot signature returns; the browser posts it straight to Cloudinary. */
export const StoreScreenshotUploadSignatureResponseSchema = AssetUploadSignatureResponseSchema
export type StoreScreenshotUploadSignatureResponse = z.infer<typeof StoreScreenshotUploadSignatureResponseSchema>

/** The Cloudinary metadata a client-side upload reports back. */
export const StoreScreenshotUploadedImageSchema = z.object({
  cloudinaryPublicId: z.string().min(1),
  cloudinaryVersion: z.string().min(1),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  mimeType: z.string().min(1),
  sizeBytes: z.number().int().nonnegative()
})
export type StoreScreenshotUploadedImage = z.infer<typeof StoreScreenshotUploadedImageSchema>

/** `POST /:id/screenshots` — commit one client-uploaded image into a set. */
export const StoreScreenshotCommitRequestSchema = withDeviceClassForStore(
  z.object({ ...storeScreenshotSetTargetShape, ...StoreScreenshotUploadedImageSchema.shape })
)
export type StoreScreenshotCommitRequest = z.infer<typeof StoreScreenshotCommitRequestSchema>

/** The committed image and the set it now belongs to, so a panel re-renders without a refetch. */
export const StoreScreenshotCommitResponseSchema = z.object({
  screenshot: StoreScreenshotSummarySchema,
  set: StoreScreenshotSetSchema
})
export type StoreScreenshotCommitResponse = z.infer<typeof StoreScreenshotCommitResponseSchema>

/** `PATCH /:id/screenshots/order`. `orderedIds` must be a permutation of the set; the service checks that. */
export const StoreScreenshotOrderRequestSchema = withDeviceClassForStore(
  z.object({ ...storeScreenshotSetTargetShape, orderedIds: z.array(z.string().min(1)).min(1) })
)
export type StoreScreenshotOrderRequest = z.infer<typeof StoreScreenshotOrderRequestSchema>

export const StoreScreenshotOrderResponseSchema = z.object({ set: StoreScreenshotSetSchema })
export type StoreScreenshotOrderResponse = z.infer<typeof StoreScreenshotOrderResponseSchema>

/** `DELETE /:id/screenshots/:screenshotId`. Positions are compacted, so the set comes back. */
export const StoreScreenshotRemoveResponseSchema = z.object({
  removedId: z.string(),
  set: StoreScreenshotSetSchema
})
export type StoreScreenshotRemoveResponse = z.infer<typeof StoreScreenshotRemoveResponseSchema>

/**
 * `POST /:id/screenshots/copy` — carry one line's sets forward (R7, R20).
 *
 * Both versions are read as lines by their `major.minor`. A `fromVersion` that
 * names no line copies the pre-line rows kept under exactly that version, so a
 * set the backfill left off every line can still be carried onto one.
 *
 * `store` absent means both stores. The copy is refused when any set it would
 * write already has images, and copied rows never carry the source's
 * `providerAssetId`.
 */
export const StoreScreenshotCopyRequestSchema = z.object({
  fromVersion: StoreScreenshotVersionSchema,
  toVersion: StoreScreenshotVersionSchema,
  store: StoreListingStoreSchema.optional()
})
export type StoreScreenshotCopyRequest = z.infer<typeof StoreScreenshotCopyRequestSchema>

export const StoreScreenshotCopyResponseSchema = z.object({
  fromVersion: z.string(),
  toVersion: z.string(),
  copied: z.number().int().nonnegative(),
  sets: z.array(StoreScreenshotSetSchema)
})
export type StoreScreenshotCopyResponse = z.infer<typeof StoreScreenshotCopyResponseSchema>

// =============================================================================
// Internal lane (R14, KTD8)
// =============================================================================

/**
 * An image handed to the internal lane, discriminated on `source`:
 *
 *  - `cloudinary`: the caller already uploaded and reports the metadata. Same
 *    trust-the-caller exposure as the super-admin lane.
 *  - `url`: the backend fetches `sourceUrl` through its guard and uploads it.
 *
 * There are two arms, and there was nearly a third. KTD8 also described a body
 * carrying the image bytes themselves, as base64 in JSON and as a multipart file
 * part on the single-commit route. It was cut before this shipped. Multipart is
 * not this repository's upload convention: multer survives on the two
 * inbound-email webhooks and nowhere else, and everything else uploads direct to
 * S3 or to Cloudinary. That multer is already a dependency is true, and it is
 * the wrong reason to add an ingress surface. The base64 form went with it
 * rather than leave a bytes-in-the-body path that the 5 MB JSON body limit caps
 * at roughly 3.7 MB — below Apple's own 15 MB rule, so it could not carry the
 * images it existed for.
 *
 * R14 still holds: `url` is what lets a script or an AI workflow write a set
 * without ever talking to Cloudinary. It is also the arm to prefer, because it
 * is validated against what Cloudinary MEASURED — a caller cannot commit a
 * wrong-size image by misreporting its metadata, which the `cloudinary` arm
 * trusts it not to do.
 */
const InternalCloudinaryImageSchema = StoreScreenshotUploadedImageSchema.extend({
  source: z.literal('cloudinary')
})

const InternalUrlImageSchema = z.object({
  source: z.literal('url'),
  sourceUrl: z.string().url().startsWith('https://').max(2048)
})

export const InternalStoreScreenshotImageSchema = z.discriminatedUnion('source', [
  InternalCloudinaryImageSchema,
  InternalUrlImageSchema
])
export type InternalStoreScreenshotImage = z.infer<typeof InternalStoreScreenshotImageSchema>

/** `POST /internal/mobile-apps/:id/screenshots` — one image, from either source. JSON only. */
export const InternalStoreScreenshotCommitRequestSchema = withDeviceClassForStore(
  z.discriminatedUnion('source', [
    InternalCloudinaryImageSchema.extend(storeScreenshotSetTargetShape),
    InternalUrlImageSchema.extend(storeScreenshotSetTargetShape)
  ])
)
export type InternalStoreScreenshotCommitRequest = z.infer<typeof InternalStoreScreenshotCommitRequestSchema>

/**
 * `POST /internal/mobile-apps/:id/screenshots/batch`.
 *
 * Every image is validated and uploaded before any row changes. With
 * `replaceSet`, the transaction swaps the set atomically, so a failure part way
 * through leaves the old set intact.
 */
export const InternalStoreScreenshotBatchRequestSchema = withDeviceClassForStore(
  z.object({
    ...storeScreenshotSetTargetShape,
    // The UPPER bound is not here, and that is deliberate. A Zod `too_big` is a
    // generic 400; R5 wants the rule and the fix, and the exact ceiling is
    // per-class. So the handler refuses an over-cap array by calling
    // `validateScreenshotCount` itself, before `resolveInternalScreenshotImages`
    // fetches and uploads a single image — see the internal batch handler.
    images: z.array(InternalStoreScreenshotImageSchema).min(1),
    replaceSet: z.boolean().optional()
  })
)
export type InternalStoreScreenshotBatchRequest = z.infer<typeof InternalStoreScreenshotBatchRequestSchema>

export const InternalStoreScreenshotBatchResponseSchema = z.object({
  committed: z.array(StoreScreenshotSummarySchema),
  removed: z.number().int().nonnegative(),
  set: StoreScreenshotSetSchema
})
export type InternalStoreScreenshotBatchResponse = z.infer<typeof InternalStoreScreenshotBatchResponseSchema>
